import { ChangeEvent, MouseEvent, SyntheticEvent, useState } from 'react';

import { DownloadOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterListIcon from '@mui/icons-material/FilterListTwoTone';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Box,
    CardContent,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { SHORT_DATE_FORMAT } from '@npi/buddy-utils';
import { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { downloadCSV } from 'src/components/TableList/downloadTableCsv';
import {
    disableCopyEventHandler,
    disableCutEventHandler,
} from 'src/hooks/useEventListener/eventHandlers/index';
import {
    ArrangementOrder,
    EnhancedTableHeadProps,
    GetComparator,
    HeadCell,
    KeyedObject,
} from 'src/types';
import { FilterOptionsType } from 'src/utils/tableUtils';
import { formatDate } from 'src/utils/utils';

import TableBackground from '../../assets/images/backgrounds/Table.jpg';
import Button from '../Button';
import MainCard from '../MainCard/MainCard';

import TableFilter from './TableFilter';

// table sort
const descendingComparator = (a: KeyedObject, b: KeyedObject, orderBy: string) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
};

const getComparator: GetComparator = (order, orderBy) => {
    return order === 'desc'
        ? (a, b) => {
              return descendingComparator(a, b, orderBy);
          }
        : (a, b) => {
              return -descendingComparator(a, b, orderBy);
          };
};

const stableSort = (array: unknown[], comparator: (a: unknown, b: unknown) => number) => {
    const stabilizedThis =
        array?.map((el: unknown, index: number) => {
            return [el, index];
        }) || [];
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0] as unknown, b[0] as unknown);
        if (order !== 0) return order;
        return (a[1] as number) - (b[1] as number);
    });
    return stabilizedThis.map((el) => {
        return el[0];
    });
};

// ==============================|| TABLE HEADER ||============================== //
const EnhancedTableHead = ({
    order,
    orderBy,
    onRequestSort,
    headCells,
    enableActions = true,
}: EnhancedTableHeadProps) => {
    const createSortHandler = (property: string) => {
        return (event: SyntheticEvent<Element, Event>) => {
            onRequestSort(event, property);
        };
    };
    const headersToRender = enableActions ? [...headCells, { header: 'action' }] : headCells;

    return (
        <TableHead sx={{ backgroundColor: '#FFFAE5' }}>
            <TableRow sx={{ color: '#1D1D1B' }}>
                {headersToRender.map((headCell: HeadCell) => {
                    if (headCell?.header !== 'action') {
                        return (
                            <th
                                className="MuiTableCell-root MuiTableCell-head MuiTableCell-alignLeft MuiTableCell-sizeMedium"
                                key={headCell.id ?? uuidv4()}
                                scope="col"
                                align={headCell?.align ?? 'left'}
                                style={{
                                    borderBottom: '1px solid #FDC800',
                                    minWidth: headCell?.minWidth ?? 170,
                                    padding: '16px',
                                    lineHeight: '1.5rem',
                                    textAlign: 'left',
                                    verticalAlign: 'inherit',
                                    display: 'table-cell',
                                    letterSpacing: '0em',
                                }}
                                // sortDirection={orderBy === headCell.id ? order : false}
                            >
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={createSortHandler(headCell.id)}
                                    sx={{ color: '#1D1D1B', fontWeight: 'bold' }}
                                    hideSortIcon
                                >
                                    {headCell.label}
                                    {orderBy === headCell.id ? (
                                        <Box component="span" sx={visuallyHidden}>
                                            {order === 'desc'
                                                ? 'sorted descending'
                                                : 'sorted ascending'}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            </th>
                        );
                    }

                    return (
                        <th
                            className="MuiTableCell-root MuiTableCell-head MuiTableCell-alignLeft MuiTableCell-sizeMedium"
                            key="action"
                            scope="col"
                            style={{
                                color: '#1D1D1B',
                                borderBottom: '1px solid #FDC800',
                                minWidth: headCell?.minWidth ?? 170,
                                padding: '16px',
                                lineHeight: '1.5rem',
                                textAlign: 'left',
                                verticalAlign: 'inherit',
                                display: 'table-cell',
                                letterSpacing: '0em',
                            }}
                        />
                    );
                })}
            </TableRow>
        </TableHead>
    );
};

// ==============================|| CELL ||============================== //

const renderCell = ({ row, header, index, handleCellClick, disableCopy, disableCut }) => {
    // Cell is a date, render the date
    const getNestedProp = (row: any, id: string) => {
        const nestedProps = id.split('.');
        let value = row;
        nestedProps.forEach((prop) => {
            if (value && typeof value === 'object') {
                value = value[prop];
            }
        });
        return value;
    };

    if (header?.label?.includes('Date') || header.type === 'Date') {
        const dateValue = row?.[header.id];
        const date = /^\d{13}$/.test(dateValue) ? parseInt(dateValue) : dateValue;

        return (
            <td
                className="MuiTableCell-root MuiTableCell-body MuiTableCell-alignLeft MuiTableCell-sizeMedium"
                key={index}
                onCopy={disableCopy}
                onCut={disableCut}
                align={header.align ?? 'left'}
                style={{
                    minWidth: header.minWidth ?? 170,
                    cursor: header.id === 'users' ? 'pointer' : null,
                    letterSpacing: '0em',
                    fontWeight: 400,
                    lineHeight: '1.5em',
                    fontFamily: 'Outfit',
                    fontSize: '0.875rem',
                    display: 'table-cell',
                    verticalAlign: 'inherit',
                    borderBottom: '1px solid rgb(236, 236, 236)',
                    padding: '16px',
                    color: 'rgb(29, 29, 27)',
                    textAlign: 'left',
                    borderTopColor: 'rgb(236, 236, 236)',
                    borderRightColor: 'rgb(236, 236, 236)',
                    borderLeftColor: 'rgb(236, 236, 236)',
                }}
                onClick={
                    header.id === 'users'
                        ? (event) => {
                              return handleCellClick(event, row);
                          }
                        : () => {}
                }
            >
                {(() => {
                    const { __typename } = row;
                    if (!new Date(date).toJSON() && __typename !== 'AuditLog') {
                        return null;
                    }

                    if (__typename === 'AuditLog') {
                        return date;
                    }
                    return date
                        ? formatDate({ date: new Date(date), format: SHORT_DATE_FORMAT })
                        : '';
                })()}
            </td>
        );
    }

    // Cell is a Description for Notes, render the Description
    if (header?.label?.includes('Description')) {
        return (
            <td
                style={{
                    borderBottom: '1px solid rgb(236, 236, 236)',
                }}
                key={index}
                onCopy={disableCopy}
                onCut={disableCut}
            >
                <Box
                    sx={{
                        minWidth: header?.minWidth ?? 100,
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                        wordBreak: 'break-word',
                    }}
                >
                    {getNestedProp(row, header.id) ?? '-'}
                </Box>
            </td>
        );
    }

    // Render cell normal
    return (
        <td
            key={index}
            align={header.align ?? 'left'}
            onCopy={disableCopy}
            onCut={disableCut}
            style={{
                minWidth: header.minWidth ?? 170,
                cursor: header.id === 'users' ? 'pointer' : null,
                letterSpacing: '0em',
                fontWeight: 400,
                lineHeight: '1.5em',
                fontFamily: 'Outfit',
                fontSize: '0.875rem',
                display: 'table-cell',
                verticalAlign: 'inherit',
                borderBottom: '1px solid rgb(236, 236, 236)',
                padding: '16px',
                color: 'rgb(29, 29, 27)',
                textAlign: 'left',
                borderTopColor: 'rgb(236, 236, 236)',
                borderRightColor: 'rgb(236, 236, 236)',
                borderLeftColor: 'rgb(236, 236, 236)',
            }}
            onClick={
                header.id === 'users'
                    ? (event) => {
                          return handleCellClick(event, row);
                      }
                    : () => {}
            }
        >
            {getNestedProp(row, header.id) ?? '-'}
        </td>
    );
};

// ==============================|| TABLE LIST ||============================== //

interface TableListProps {
    data: unknown[];
    exportData?: Record<string, string>[];
    headers: HeadCell[];
    csvHeaders?: HeadCell[];
    title?: string;
    addRecordButtonText?: string;
    addRecordSecondButtonText?: string;
    addRecordCallBack?: () => void;
    addSecondRecordCallBack?: () => void;
    deleteRecordCallBack?: (id: number | string | null, row: object) => void;
    handleEditRecord?: (record: object | null) => void;
    handleViewRecord?: (record: object | null) => void;
    filterOptions?: FilterOptionsType;
    updateTableFilters?: (filter: string, newFilterValue: string) => void;
    enableFilter?: boolean;
    handleCellClick?: (event: any, rowData: KeyedObject) => void;
    isTableModal?: boolean;
    enableExport?: boolean;
    enableEdit?: boolean;
    enableDelete?: boolean;
    enableAddRecord?: boolean;
    isDashboardTable?: boolean;
    isHistoryTable?: boolean;
    isAuditLogTable?: boolean;
    enableFilterByDateRange?: boolean;
    disableContentCopy?: boolean;
    disableContentCut?: boolean;
    hasAddRecordSecondButtonPermission?: string;
    hasAddRecordButtonPermission?: string;
    emptyRowColSpan?: number;
    minRowsPerPage?: number;
    tableDataTestId?: string;
    enableActions?: boolean;
}

const TableList = ({
    data = [],
    exportData = [],
    headers,
    title = '',
    csvHeaders,
    addRecordButtonText = 'Add Record',
    addRecordSecondButtonText,
    addRecordCallBack,
    addSecondRecordCallBack,
    deleteRecordCallBack,
    handleEditRecord,
    handleViewRecord,
    filterOptions,
    updateTableFilters,
    enableFilter = false,
    enableExport,
    enableEdit = false || typeof handleEditRecord === 'function',
    enableDelete = false || typeof deleteRecordCallBack === 'function',
    enableAddRecord = false || typeof addRecordCallBack === 'function',
    handleCellClick,
    isTableModal,
    isDashboardTable,
    isHistoryTable,
    isAuditLogTable,
    enableFilterByDateRange,
    disableContentCopy,
    disableContentCut,
    hasAddRecordSecondButtonPermission,
    hasAddRecordButtonPermission,
    emptyRowColSpan = 6,
    tableDataTestId = `${title}-table`,
    minRowsPerPage = 5,
    enableActions = true,
}: TableListProps) => {
    const theme = useTheme();
    const [order, setOrder] = useState<ArrangementOrder>('asc');
    const [orderBy, setOrderBy] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(minRowsPerPage);
    const [showFilters, setShowFilters] = useState<boolean>(false);

    const [showExportOptions, setShowExportOptions] = useState(false);

    const handleShowFilter = () => {
        setShowFilters(!showFilters);
        updateTableFilters('clear', '-');
    };

    const handleRequestSort = (event: SyntheticEvent<Element, Event>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined
    ) => {
        event?.target.value && setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const handleExportRecords = (type: 'filtered' | 'all') => {
        let recordsToExport: Record<string, string>[];

        if (type === 'all') {
            if (exportData && exportData.length) {
                recordsToExport = exportData as Record<string, string>[];
            } else {
                recordsToExport = data as Record<string, string>[];
            }
        } else {
            if (exportData && exportData.length) {
                // TODO: fix filteredExport
                // recordsToExport = filteredExportData as Record<string, string>[]
            } else {
                recordsToExport = data as Record<string, string>[];
            }
        }

        const headersToUse = title === 'Audit Logs Lists' ? csvHeaders : headers;
        downloadCSV(recordsToExport, headersToUse, title);

        setShowExportOptions(false);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

    // Only show button if NOT the Audit Log table
    const shouldShowAddRecordButton = !isAuditLogTable && enableAddRecord;

    return (
        <MainCard content={false} sx={{ boxShadow: theme.customShadows.tableListBoxShadow }}>
            {(!isTableModal || !isDashboardTable) && (
                <CardContent
                    sx={{
                        backgroundImage: `url(${TableBackground})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        borderRadius: '16px 0px',
                    }}
                >
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={enableFilterByDateRange ? 4 : 6}>
                            <TextField
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                ) => {
                                    if (updateTableFilters) {
                                        updateTableFilters('search', event?.target?.value);
                                        handleChangePage(null, 0);
                                    }
                                }}
                                placeholder="Search table"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderRadius: '4px',
                                        border: ' 1px solid #F3A256',
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={enableFilterByDateRange ? 8 : 6}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                {enableFilter && (
                                    <Tooltip title="Filter">
                                        <IconButton
                                            size="large"
                                            onClick={handleShowFilter}
                                            color="primary"
                                        >
                                            <FilterListIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {enableExport && (
                                    <Tooltip title="Export">
                                        <IconButton
                                            size="large"
                                            onClick={() => {
                                                setShowExportOptions(!showExportOptions);
                                            }}
                                            color="primary"
                                        >
                                            <DownloadOutlined />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Button
                                    label={addRecordSecondButtonText}
                                    onClick={addSecondRecordCallBack}
                                    startIcon={<AddIcon />}
                                    variant="npi-primary"
                                    permission={hasAddRecordSecondButtonPermission}
                                    hidden={!addRecordSecondButtonText}
                                />
                                <Button
                                    label={addRecordButtonText}
                                    onClick={addRecordCallBack}
                                    startIcon={<AddIcon />}
                                    variant="npi-primary"
                                    hidden={!shouldShowAddRecordButton}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                    {showFilters && (
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                            alignItems="flex-end"
                        >
                            <TableFilter
                                filterOptions={filterOptions}
                                updateFilters={(
                                    newFilter: string,
                                    filterValue: string | { from: Dayjs; to: Dayjs }
                                ) => {
                                    if (updateTableFilters) {
                                        updateTableFilters(newFilter, filterValue);
                                        handleChangePage(null, 0);
                                    }
                                }}
                            />
                            <Button
                                variant="text"
                                onClick={handleShowFilter}
                                startIcon={<CloseOutlinedIcon sx={{ color: '#F39E4E' }} />}
                                sx={{
                                    width: '200px',
                                    color: '#575757',
                                    height: '64px',
                                }}
                            >
                                Clear
                            </Button>
                        </Stack>
                    )}
                    {showExportOptions && (
                        <Stack
                            direction="row"
                            justifyContent="right"
                            spacing={2}
                            alignItems="ex-end"
                        >
                            <Button
                                variant="text"
                                onClick={() => {
                                    return handleExportRecords('all');
                                }}
                                startIcon={<DownloadOutlined />}
                                sx={{
                                    width: '200px',
                                    color: '#575757',
                                    height: '64px',
                                }}
                            >
                                Download all data
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setShowExportOptions(!showExportOptions);
                                }}
                                startIcon={<CloseOutlinedIcon sx={{ color: '#F39E4E' }} />}
                                sx={{
                                    width: '200px',
                                    color: '#575757',
                                    height: '64px',
                                }}
                            >
                                Close Export
                            </Button>
                        </Stack>
                    )}
                </CardContent>
            )}
            {/* table */}
            <TableContainer data-testid={tableDataTestId}>
                <Table
                    aria-labelledby="tableTitle"
                    sx={{
                        tableLayout: 'fixed', // Makes the table layout fixed for better responsiveness
                        width: '100%',
                        '@media (max-width: 600px)': {
                            display: 'block', // Make table block for small screens
                        },
                    }}
                >
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                        headCells={headers}
                        title={title}
                        enableActions={enableActions}
                    />
                    <TableBody data-testid="table-list">
                        {stableSort(data, getComparator(order, orderBy))
                            ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            ?.map((row, index) => {
                                if (typeof row === 'number') return null;
                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        key={index}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? 'white' : '#FFFCF0',
                                        }}
                                    >
                                        {headers?.map((header, index) => {
                                            return renderCell({
                                                row,
                                                header,
                                                index,
                                                handleCellClick,
                                                disableCopy: disableContentCopy
                                                    ? disableCopyEventHandler
                                                    : null,
                                                disableCut: disableContentCut
                                                    ? disableCutEventHandler
                                                    : null,
                                            });
                                        })}
                                        {isHistoryTable
                                            ? null
                                            : enableActions &&
                                              (!isTableModal || isDashboardTable) && (
                                                  <td
                                                      style={{
                                                          padding: '16px',
                                                          borderBottom:
                                                              '1px solid rgb(236, 236, 236)',
                                                      }}
                                                  >
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              height: '100%',
                                                              justifyContent: 'flex-end',
                                                          }}
                                                      >
                                                          {!isAuditLogTable && (
                                                              <IconButton
                                                                  aria-label="view"
                                                                  onClick={() => {
                                                                      handleViewRecord(row);
                                                                  }}
                                                              >
                                                                  <VisibilityOutlinedIcon />
                                                              </IconButton>
                                                          )}

                                                          {enableEdit && (
                                                              <IconButton aria-label="edit">
                                                                  <EditOutlinedIcon
                                                                      onClick={() => {
                                                                          handleEditRecord(row);
                                                                      }}
                                                                  />
                                                              </IconButton>
                                                          )}
                                                          {enableDelete && (
                                                              <IconButton
                                                                  aria-label="delete"
                                                                  disabled={
                                                                      row?.status?.props?.status ===
                                                                      'SUSPENDED'
                                                                  }
                                                              >
                                                                  <DeleteOutlineOutlinedIcon
                                                                      onClick={() => {
                                                                          return deleteRecordCallBack(
                                                                              row.id,
                                                                              row
                                                                          );
                                                                      }}
                                                                  />
                                                              </IconButton>
                                                          )}
                                                      </Box>
                                                  </td>
                                              )}
                                    </TableRow>
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow
                                style={{
                                    height: 53 * emptyRows,
                                }}
                            >
                                <td colSpan={emptyRowColSpan} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* table pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </MainCard>
    );
};
export default TableList;
